import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';

const LocationCard = ({ location, onGetDirections, onCallLocation, isSelected, onSelect, index }) => {
  const { isDarkMode } = useTheme();
  
  const handleGetDirections = () => {
    onGetDirections(location);
  };

  const handleCallLocation = () => {
    onCallLocation(location?.phone);
  };

  // Different background gradients for each card - with dark mode support
  const getCardBackground = (index) => {
    if (isDarkMode) {
      const darkBackgrounds = [
        'bg-gradient-to-br from-blue-900/30 to-blue-800/30',
        'bg-gradient-to-br from-green-900/30 to-green-800/30', 
        'bg-gradient-to-br from-purple-900/30 to-purple-800/30',
        'bg-gradient-to-br from-orange-900/30 to-orange-800/30',
        'bg-gradient-to-br from-pink-900/30 to-pink-800/30',
        'bg-gradient-to-br from-indigo-900/30 to-indigo-800/30',
        'bg-gradient-to-br from-teal-900/30 to-teal-800/30',
        'bg-gradient-to-br from-amber-900/30 to-amber-800/30',
        'bg-gradient-to-br from-rose-900/30 to-rose-800/30',
        'bg-gradient-to-br from-cyan-900/30 to-cyan-800/30'
      ];
      return darkBackgrounds[index % darkBackgrounds.length];
    } else {
      const lightBackgrounds = [
        'bg-gradient-to-br from-blue-50 to-blue-100',
        'bg-gradient-to-br from-green-50 to-green-100', 
        'bg-gradient-to-br from-purple-50 to-purple-100',
        'bg-gradient-to-br from-orange-50 to-orange-100',
        'bg-gradient-to-br from-pink-50 to-pink-100',
        'bg-gradient-to-br from-indigo-50 to-indigo-100',
        'bg-gradient-to-br from-teal-50 to-teal-100',
        'bg-gradient-to-br from-amber-50 to-amber-100',
        'bg-gradient-to-br from-rose-50 to-rose-100',
        'bg-gradient-to-br from-cyan-50 to-cyan-100'
      ];
      return lightBackgrounds[index % lightBackgrounds.length];
    }
  };

  return (
    <div 
      className={`rounded-xl p-6 shadow-elevation-1 transition-all duration-300 cursor-pointer hover:shadow-elevation-3 hover:-translate-y-1 border-2 ${
        isSelected 
          ? isDarkMode 
            ? 'border-primary bg-gradient-to-br from-primary/20 to-primary/10' 
            : 'border-primary bg-gradient-to-br from-primary/5 to-primary/10'
          : isDarkMode
            ? `border-slate-700/50 ${getCardBackground(index)}`
            : `border-transparent ${getCardBackground(index)}`
      }`}
      onClick={() => onSelect(location)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className={`text-lg font-semibold mb-1 ${
            isDarkMode ? 'text-white' : 'text-foreground'
          }`}>
            {location?.name}
          </h3>
          <div className={`flex items-start text-sm mb-2 ${
            isDarkMode ? 'text-slate-300' : 'text-muted-foreground'
          }`}>
            <Icon name="MapPin" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <span>{location?.address}</span>
              {location?.locationLandmark && (
                <span className="block mt-1 text-xs opacity-90">Landmark: {location?.locationLandmark}</span>
              )}
            </div>
          </div>
          <div className={`flex items-center text-sm ${
            isDarkMode ? 'text-slate-300' : 'text-muted-foreground'
          }`}>
            <Icon name="Clock" size={16} className="mr-2" />
            <span>{location?.hours}</span>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium mb-2 ${
            isDarkMode 
              ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30' 
              : 'bg-primary/10 text-primary'
          }`}>
            <Icon name="Navigation" size={12} className="mr-1" />
            {location?.distance}
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            location?.status === 'Open' 
              ? isDarkMode 
                ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                : 'bg-success/10 text-success'
              : isDarkMode
                ? 'bg-red-600/20 text-red-400 border border-red-600/30'
                : 'bg-error/10 text-error'
          }`}>
            {location?.status}
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {location?.services?.map((service, index) => {
            const serviceColors = isDarkMode ? {
              'Wash & Fold': 'bg-blue-600/20 text-blue-400 border-blue-600/30',
              'Dry Clean': 'bg-green-600/20 text-green-400 border-green-600/30',
              'Iron & Press': 'bg-purple-600/20 text-purple-400 border-purple-600/30',
              'Pickup & Delivery': 'bg-orange-600/20 text-orange-400 border-orange-600/30'
            } : {
              'Wash & Fold': 'bg-blue-100 text-blue-700 border-blue-200',
              'Dry Clean': 'bg-green-100 text-green-700 border-green-200',
              'Iron & Press': 'bg-purple-100 text-purple-700 border-purple-200',
              'Pickup & Delivery': 'bg-orange-100 text-orange-700 border-orange-200'
            };
            const colorClass = serviceColors[service] || (isDarkMode ? 'bg-gray-600/20 text-gray-400 border-gray-600/30' : 'bg-gray-100 text-gray-700 border-gray-200');
            
            return (
              <span 
                key={index}
                className={`${colorClass} px-3 py-1 rounded-full text-xs font-medium border transition-all duration-300 hover:scale-105`}
              >
                {service}
              </span>
            );
          })}
        </div>
        
        {location?.specialInstructions && (
          <div className={`rounded-lg p-3 ${
            isDarkMode 
              ? 'bg-slate-700/50 border border-slate-600/50' 
              : 'bg-accent/10 border border-accent/20'
          }`}>
            <div className="flex items-start">
              <Icon name="Info" size={16} className={`mr-2 mt-0.5 flex-shrink-0 ${
                isDarkMode ? 'text-blue-400' : 'text-accent'
              }`} />
              <p className={`text-sm ${
                isDarkMode ? 'text-slate-200' : 'text-accent-foreground'
              }`}>
                {location?.specialInstructions}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Navigation"
          iconPosition="left"
          onClick={handleGetDirections}
          className="flex-1"
        >
          Directions
        </Button>
        <Button
          variant="default"
          size="sm"
          iconName="Phone"
          iconPosition="left"
          onClick={handleCallLocation}
          className="flex-1"
        >
          Call
        </Button>
      </div>
    </div>
  );
};

export default LocationCard;